/* redux-coreapi version 0.1.1 */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) : typeof define === 'function' && define.amd ? define(['exports'], factory) : factory(global.ReduxCoreAPI = {});
})(this, function (exports) {
  'use strict';
  /**
   * Dispatched by {@link action} to initiate a Core API action request.
   */

  var CLIENT_ACTION = 'redux-coreapi:CLIENT_ACTION';
  /**
   * Contains the data for an {@link action} call.
   *
   * @typedef {Object} ClientAction
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_ACTION}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {coreapi.Document} payload.document - The Core API schema document
   *   describing the API to which the request will be sent.
   * @property {string[]} payload.keys - An array of strings describing the
   *   action to request. For example, `["users", "list"]` or
   *   `["widgets", 0, "update"]`.
   * @property {Object} [payload.params] - Optional data to include with the
   *   request. The structure of the object depends on the action being
   *   requested.
   */

  /**
   * Dispatched when a Core API action request fails.
   * @type string
   */

  var CLIENT_ACTION_FAILURE = 'redux-coreapi:CLIENT_ACTION_FAILURE';
  /**
   * Contains the data for a failed Core API action request.
   *
   * @typedef {Object} ClientActionFailure
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_ACTION_FAILURE}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request was be made.
   * @property {coreapi.Document} payload.document - The Core API schema document
   *   describing the API to which the request was sent.
   * @property {string[]} payload.keys - An array of strings describing the
   *   action that was requested. For example, `["users", "list"]` or
   *   `["widgets", 0, "update"]`.
   * @property {Object} [payload.params] - Optional data included with the
   *   request. The structure of the object depends on the action requested.
   * @property {Error} payload.error - The error encountered by the action
   *   request.
   */

  /**
   * Dispatched immediately before a Core API action request is sent.
   * @type string
   */

  var CLIENT_ACTION_REQUEST = 'redux-coreapi:CLIENT_ACTION_REQUEST';
  /**
   * Contains the data for a Core API action request.
   *
   * @typedef {Object} ClientActionRequest
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_ACTION_REQUEST}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {coreapi.Document} payload.document - The Core API schema document
   *   describing the API to which the request will be sent.
   * @property {string[]} payload.keys - An array of strings describing the
   *   action to request. For example, `["users", "list"]` or
   *   `["widgets", 0, "update"]`.
   * @property {Object} [payload.params] - Optional data to include with the
   *   request. The structure of the object depends on the action being
   *   requested.
   */

  /**
   * Dispatched when a Core API action request succeeds.
   * @type string
   */

  var CLIENT_ACTION_SUCCESS = 'redux-coreapi:CLIENT_ACTION_SUCCESS';
  /**
   * Contains the response data for a successful Core API action request.
   *
   * @typedef {Object} ClientActionSuccess
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_ACTION_SUCCESS}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request was be made.
   * @property {coreapi.Document} payload.document - The Core API schema document
   *   describing the API to which the request was sent.
   * @property {string[]} payload.keys - An array of strings describing the
   *   action that was requested. For example, `["users", "list"]` or
   *   `["widgets", 0, "update"]`.
   * @property {Object} [payload.params] - Optional data included with the
   *   request. The structure of the object depends on the action requested.
   * @property {Object} payload.result - The response data from the request.
   */

  /**
   * Dispatched by {@link get} to initiate a Core API document request.
   */

  var CLIENT_GET = 'redux-coreapi:CLIENT_GET';
  /**
   * Contains the data for a {@link get} call.
   *
   * @typedef {Object} ClientGet
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_GET}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {string} payload.url - The URL of the document being requested.
   */

  /**
   * Dispatched when a Core API document request fails.
   * @type string
   */

  var CLIENT_GET_FAILURE = 'redux-coreapi:CLIENT_GET_FAILURE';
  /**
   * Contains the data for a failed Core API document request.
   *
   * @typedef {Object} ClientGetFailure
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_GET_FAILURE}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {string} payload.url - The URL of the document that was requested.
   * @property {Error} payload.error - The error encountered by the document
   *   request.
   */

  /**
   * Dispatched immediately before a Core API document request is sent.
   * @type string
   */

  var CLIENT_GET_REQUEST = 'redux-coreapi:CLIENT_GET_REQUEST';
  /**
   * Contains the data for a Core API document request.
   *
   * @typedef {Object} ClientGetRequest
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_GET_REQUEST}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {string} payload.url - The URL of the document being requested.
   */

  /**
   * Dispatched when a Core API document request succeeds.
   * @type string
   */

  var CLIENT_GET_SUCCESS = 'redux-coreapi:CLIENT_GET_SUCCESS';
  /**
   * Contains the response data for a successful Core API document request.
   *
   * @typedef {Object} ClientGetSuccess
   * @property {string} type - The type of the Redux action. Must always be set
   *   to {@see CLIENT_GET_SUCCESS}.
   * @property {Object} payload - An object containing the data for the request.
   * @property {coreapi.Client} payload.client - The Core API client instance
   *   with which the request will be made.
   * @property {string} payload.url - The URL of the document that was requested.
   * @property {coreapi.Document} payload.document - The Core API document.
   */

  /**
   * Make a Core API action request.
   *
   * @param {coreapi.Client} client - The Core API client instance with which the
   *   request will be made.
   * @param {coreapi.Document} document - The Core API document describing
   *   the API to which the request will be sent.
   * @param {string[]} keys - An array of strings describing the action to
   *   request. For example, `["users", "list"]` or `["widgets", 0, "update"]`.
   * @param {Object} [params] - Optional data to include with the request. The
   *   structure of the object depends on the action being requested.
   * @returns {ClientAction} - A Redux action for the Core API action request.
   *   If an argument contains invalid data, a {@see ClientActionError} will be
   *   returned instead.
   */

  function action(client, document, keys, params) {
    try {
      validateClient(client);
    } catch (error) {
      return {
        type: CLIENT_ACTION_FAILURE,
        payload: {
          client: client,
          document: document,
          keys: keys,
          params: params,
          error: error
        }
      };
    }

    if (!document || !document.url || !document.content) {
      return {
        type: CLIENT_ACTION_FAILURE,
        payload: {
          client: client,
          document: document,
          keys: keys,
          params: params,
          error: new TypeError(document + ' is not a valid Core API document.')
        }
      };
    }

    if (!Array.isArray(keys)) {
      return {
        type: CLIENT_ACTION_FAILURE,
        payload: {
          client: client,
          document: document,
          keys: keys,
          params: params,
          error: new TypeError(keys + ' is not a valid key array.')
        }
      };
    }

    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = keys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var key = _step.value;

        if (typeof key !== 'string') {
          return {
            type: CLIENT_ACTION_FAILURE,
            payload: {
              client: client,
              document: document,
              keys: keys,
              params: params,
              error: new TypeError(key + ' is not a valid key.')
            }
          };
        }
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    return {
      type: CLIENT_ACTION,
      payload: {
        client: client,
        document: document,
        keys: keys,
        params: params
      }
    };
  }
  /**
   * Make a Core API document request.
   *
   * @param {coreapi.Client} client - The Core API client instance with which the
   *   request will be made.
   * @property {string} url - The URL of the document to request.
   * @returns {ClientGet} - A Redux action for the Core API get request.  If an
   *   argument contains invalid data, a {@see ClientGetError} will be returned
   *   instead.
   */


  function get(client, url) {
    try {
      validateClient(client);
    } catch (error) {
      return {
        type: CLIENT_GET_FAILURE,
        payload: {
          client: client,
          url: url,
          error: error
        }
      };
    }

    if (typeof url !== 'string') {
      return {
        type: CLIENT_GET_FAILURE,
        payload: {
          client: client,
          url: url,
          error: new TypeError('URL must be a string.')
        }
      };
    }

    return {
      type: CLIENT_GET,
      payload: {
        client: client,
        url: url
      }
    };
  }
  /**
   * Ensure an object is a valid Core API client. Raises a `TypeError` if not.
   *
   * @param {} client - The object to validate.
   * @ignore
   */


  function validateClient(client) {
    var invalid = !client || typeof client.action !== 'function' || typeof client.get !== 'function';

    if (invalid) {
      throw new TypeError(client + ' is not a valid Core API client.');
    }
  }
  /**
   * Redux middleware that processes Core API action and document requests.
   *
   * For both Core API `action` and `get` requests made by calling the
   * corresponding `redux-coreapi` {@link action} and {@link get} functions,
   * the behavior is as follows:
   *
   * 1. A {@link ClientActionRequest} or {@link ClientGetRequest} is dispatched
   *    with the data used to make the request.
   * 2. The supplied `coreapi.Client` is used to asynchronously make the request.
   * 3. Upon completion, a Redux action for the result is dispatched:
   *    - On success, a {@link ClientActionSuccess} or {@link ClientGetSuccess}
   *      is dispatched with the request and response data.
   *    - On failure, a {@link ClientActionFailure} or {@link ClientGetFailure}
   *      is dispatched with the request and error data.
   *
   * Any other action types will not be processed.
   *
   * @alias coreapiMiddleware
   */
  // eslint-disable-next-line no-unused-vars


  var middleware = function middleware(store) {
    return function (next) {
      return function (action) {
        if (action.type === CLIENT_ACTION) {
          var _action$payload = action.payload,
              client = _action$payload.client,
              document = _action$payload.document,
              keys = _action$payload.keys,
              params = _action$payload.params;
          next({
            type: CLIENT_ACTION_REQUEST,
            payload: {
              client: client,
              document: document,
              keys: keys,
              params: params
            }
          });

          try {
            return client.action(document, keys, params).then(function (result) {
              return next({
                type: CLIENT_ACTION_SUCCESS,
                payload: {
                  client: client,
                  document: document,
                  keys: keys,
                  params: params,
                  result: result
                }
              });
            }, function (error) {
              return next({
                type: CLIENT_ACTION_FAILURE,
                payload: {
                  client: client,
                  document: document,
                  keys: keys,
                  params: params,
                  error: error
                }
              });
            });
          } catch (error) {
            return next({
              type: CLIENT_ACTION_FAILURE,
              payload: {
                client: client,
                document: document,
                keys: keys,
                params: params,
                error: error
              }
            });
          }
        } else if (action.type === CLIENT_GET) {
          var _action$payload2 = action.payload,
              _client = _action$payload2.client,
              url = _action$payload2.url;
          next({
            type: CLIENT_GET_REQUEST,
            payload: {
              client: _client,
              url: url
            }
          });

          try {
            return _client.get(url).then(function (document) {
              return next({
                type: CLIENT_GET_SUCCESS,
                payload: {
                  client: _client,
                  url: url,
                  document: document
                }
              });
            }, function (error) {
              return next({
                type: CLIENT_GET_FAILURE,
                payload: {
                  client: _client,
                  url: url,
                  error: error
                }
              });
            });
          } catch (error) {
            return next({
              type: CLIENT_GET_FAILURE,
              payload: {
                client: _client,
                url: url,
                error: error
              }
            });
          }
        } else {
          return next(action);
        }
      };
    };
  };
  /**
   * A minimal interface between Redux and Core API.
   */


  exports.CLIENT_ACTION = CLIENT_ACTION;
  exports.CLIENT_ACTION_FAILURE = CLIENT_ACTION_FAILURE;
  exports.CLIENT_ACTION_REQUEST = CLIENT_ACTION_REQUEST;
  exports.CLIENT_ACTION_SUCCESS = CLIENT_ACTION_SUCCESS;
  exports.CLIENT_GET = CLIENT_GET;
  exports.CLIENT_GET_FAILURE = CLIENT_GET_FAILURE;
  exports.CLIENT_GET_REQUEST = CLIENT_GET_REQUEST;
  exports.CLIENT_GET_SUCCESS = CLIENT_GET_SUCCESS;
  exports.action = action;
  exports.get = get;
  exports.coreapiMiddleware = middleware;
  Object.defineProperty(exports, '__esModule', {
    value: true
  });
});